import { useSelector } from 'react-redux'

import { digObject, snakeToTitleCase } from '@domain-group/javascript-utils'

import useReduxAction from '@hooks/useReduxAction'

const defaultThemeColors = [
  { primary: '#8361BB', border: '#8361BB' },
  { primary: '#61ABBB', border: '#61ABBB' },
  { primary: '#99BB61', border: '#99BB61' },
  { primary: '#61BB95', border: '#61BB95' },
  { primary: '#6180BB', border: '#6180BB' },
  { primary: '#BBA761', border: '#BBA761' },
]

const generateTheme = (payload: { dataKeys: string[] }) => {
  const { dataKeys } = payload || {}

  const theme = dataKeys.reduce((acc, dataKey, index) => {
    // @ts-expect-error TS7053
    acc[dataKey] = {
      colors: defaultThemeColors[index] || defaultThemeColors[0],
      title: snakeToTitleCase(dataKey),
    }

    return acc
  }, {})

  return theme
}

const generateEndPoint = (chartKey: string, entityId?: number): string => {
  const endPoints: { [key: string]: string } = {
    first_time_right_leaderboard: '/charts/task_list_items/first_time_right_leaderboard',
    task_list_items_completed_per_date_period: '/charts/task_list_items/completed_per_date_period',
    task_list_items_future_weekly_workload: '/charts/task_list_items/future_weekly_workload',
    task_list_items_overview: '/charts/task_list_items/overview.json',
    template_project_burn_down: `/charts/task_lists/${entityId}/template_project_burn_down.json`,
    template_project_overview: `/charts/task_lists/${entityId}/template_project_overview.json`,
    task_counts_by_type: '/charts/task_list_items/task_counts_by_type',
  }

  return endPoints[chartKey] || ''
}

const generateDataKey = (chartKey: string, options: { [key: string]: string | number } = {}): string => {
  const optionsString = Object.keys(options).reduce((acc, optionKey) => {
    const optionValue = options[optionKey]
    if (optionValue){
      return `${acc}-${optionKey}-${optionValue}`
    }

    return acc
  }, '')

  return `${chartKey}${optionsString}`
}

export const calculatePercentageChange = (params: {
  currentData: object,
  previousData: object,
  selectedDataPoint: string,
}) => {
  const { currentData, previousData, selectedDataPoint } = params
  const currentValueForDataPoint = digObject(currentData, selectedDataPoint, 0)
  const previousValueForDataPoint = digObject(previousData, selectedDataPoint, 0)
  const formattedValue = previousValueForDataPoint === 0 ? 1 : previousValueForDataPoint

  const percentageChange = ((currentValueForDataPoint - previousValueForDataPoint) / formattedValue) * 100

  return percentageChange
}

function useChartData(chartKey: string, options: { [key: string]: any } = {}) {
  const {
    chartDataKey: suppliedChartDataKey,
    dataKeyProps,
    entityId,
    performHttpRequests = true,
    requestOptions,
  } = options

  const excludedDataKeys = digObject(options, 'excludedDataKeys', [])

  const endpoint = generateEndPoint(chartKey, entityId)

  const chartDataKey = suppliedChartDataKey || generateDataKey(chartKey, requestOptions)

  const chartOptions = {
    chart_data_key: chartDataKey,
    ...requestOptions,
  }

  const { loading } = useReduxAction('charts', 'loadChartData', chartOptions, [chartDataKey, JSON.stringify(options)], {
    dispatchAction: action => action(endpoint, chartOptions),
    shouldPerformFn: (entityReducer) => {
      // @ts-expect-error TS2339
      const { loadedForKeys } = entityReducer

      return !!endpoint && performHttpRequests && !loadedForKeys.includes(chartDataKey)
    },
  })

  // @ts-expect-error TS18046
  const entities = useSelector(reduxState => reduxState.entities)
  const { chartData } = entities

  const dataPayload = chartData[chartDataKey] || {}

  const dataArray = digObject(dataPayload, 'data', [])

  const firstDataObject = dataArray[0] || {}
  const dataKeys = Object.keys(firstDataObject).filter(k => !excludedDataKeys.includes(k))

  const hasData = dataArray.length > 0

  return {
    chartDataKey,
    dataArray,
    dataKeyProps,
    dataKeys,
    dataPayload,
    hasData,
    loading,
    theme: generateTheme({ dataKeys }),
  }
}

export default useChartData
